/**
 * ConditionJsonControl
 *
 * The shared body of every JSONForms control that stores a `ConditionDto` as a JSON string: the lazy
 * editor, the parse in, the stringify out, and what "no condition" means on the wire.
 *
 * Two controls use it — a PlanViewRowDimension's `filterJson` (the row axis) and a PlanStatementRow's
 * `accountFilterJson` (a statement row's account filter). They differ only in which field list the
 * author may build over; everything about the round trip is the same, and it has to stay the same:
 * both are read back server-side by a resolver that treats an unparseable filter as no filter, so a
 * control that wrote a subtly different shape would silently widen a selection instead of failing.
 */

import { lazy, Suspense, useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ConditionDto, RuleContextSchemaDto } from '@/types/core/Rules';

// Lazy-loaded so react-awesome-query-builder stays in its own chunk: the renderers that use this ship
// in the shared customRenderers registry, but the editor is only needed when such a control renders.
const ConditionEditor = lazy(() =>
  import('@/core/rules/components/ConditionEditor').then((m) => ({ default: m.ConditionEditor }))
);

export interface ConditionJsonControlProps {
  /** The stored condition: a JSON string (what the schema declares), or already-parsed data. */
  value: unknown;
  /** Called with the JSON string to store, or null when the condition is empty (⇒ no filter). */
  onChange: (json: string | null) => void;
  /** `null` while the field list is still loading, so the editor shows its own loading state. */
  contextSchema: RuleContextSchemaDto | null;
  error?: string;
  /**
   * The control's own visibility, straight from JSONForms. A `visibleWhen` rule paints nothing by
   * itself — every renderer is responsible for honouring it, and a builder that keeps rendering where
   * the field does not apply is worse than a stray text box: it invites the author to compose a
   * condition the save guard then rejects.
   */
  visible?: boolean;
  /** Shown above the builder. Without it the editor is an unlabelled box among labelled fields. */
  label?: string;
}

export function ConditionJsonControl({
  value,
  onChange,
  contextSchema,
  error,
  visible = true,
  label,
}: ConditionJsonControlProps) {
  const { t } = useTranslation();

  const parsed: ConditionDto | null = useMemo(() => {
    if (!value) return null;
    if (typeof value !== 'string') return value as ConditionDto;
    try {
      return JSON.parse(value) as ConditionDto;
    } catch {
      // Stored text that is not a condition: show an empty editor rather than crash the dialog. The
      // author can then build a real one, which is the only way out — the save guard rejects the old
      // value and the read path ignores it.
      console.warn('[ConditionJsonControl] Stored value is not parseable JSON:', value);
      return null;
    }
  }, [value]);

  // After the hooks, never before: `visible` flips as the author changes the controlling field, and an
  // early return above them would change the hook count between renders.
  if (!visible) return null;

  return (
    <Box sx={{ py: 1 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Suspense fallback={<Alert severity="info">{t('common.loading', { defaultValue: 'Loading...' })}</Alert>}>
        <ConditionEditor
          value={parsed}
          onChange={(condition) => onChange(condition ? JSON.stringify(condition) : null)}
          contextSchema={contextSchema}
          error={error}
          readOnly={false}
          enableFieldComparison={false}
          extendedOperators
          compact
        />
      </Suspense>
    </Box>
  );
}
